import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getResultById } from '../data/mockApi'
import { api } from '../data/api'
import { STRINGS } from '../constants/strings'

function AnswerRow({ r, idx }) {
  const q        = r.question
  const msqPartial = q.type === 'msq' && r.creditEarned > 0 && r.creditEarned < 1
  const correct  = r.is_correct === true || r.verdict === 'Correct'
  const partial  = r.verdict === 'Partially Correct' || msqPartial
  const bgColor  = correct ? 'rgba(74,222,128,0.06)' : partial ? 'rgba(251,191,36,0.06)' : 'rgba(248,113,113,0.06)'
  const bdColor  = correct ? 'rgba(74,222,128,0.2)'  : partial ? 'rgba(251,191,36,0.2)'  : 'rgba(248,113,113,0.2)'
  const dotColor = correct ? '#4ade80' : partial ? '#fbbf24' : '#f87171'
  const label    = correct ? 'Correct' : partial ? 'Partial' : 'Wrong'

  return (
    <div className="p-4 sm:p-5" style={{ borderBottom: '1px solid var(--border)' }}>
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center gap-1.5 flex-shrink-0 pt-0.5">
          <span className="text-xs font-mono font-bold w-6 text-center" style={{ color: 'var(--text-3)' }}>{idx + 1}</span>
          <div className="w-2 h-2 rounded-full" style={{ background: dotColor }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3 mb-3">
            <p className="text-sm font-medium leading-snug" style={{ color: 'var(--text-1)' }}>{q.question}</p>
            <span className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                  style={{ background: bgColor, border: `1px solid ${bdColor}`, color: dotColor }}>{label}</span>
          </div>
          {q.type === 'mcq' && (
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span style={{ color: 'var(--text-3)' }}>Your answer:</span>
                <span className="font-medium" style={{ color: correct ? '#4ade80' : '#f87171' }}>
                  {r.user_answer ? `${r.user_answer}) ${q.options?.[r.user_answer] ?? ''}` : 'Not answered'}
                </span>
              </div>
              {!correct && (
                <div className="flex items-center gap-2">
                  <span style={{ color: 'var(--text-3)' }}>Correct:</span>
                  <span className="font-medium text-emerald-400">{q.correct_answer}) {q.options?.[q.correct_answer]}</span>
                </div>
              )}
              {q.explanation && (
                <p className="mt-2 text-xs leading-relaxed px-3 py-2 rounded-lg" style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>
                  {q.explanation}
                </p>
              )}
            </div>
          )}
          {q.type === 'msq' && (
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span style={{ color: 'var(--text-3)' }}>Your answer:</span>
                <span className="font-medium" style={{ color: correct ? '#4ade80' : partial ? '#fbbf24' : '#f87171' }}>
                  {Array.isArray(r.user_answer) && r.user_answer.length > 0 ? r.user_answer.join(', ') : 'Not answered'}
                </span>
              </div>
              {!correct && (
                <div className="flex items-center gap-2">
                  <span style={{ color: 'var(--text-3)' }}>Correct:</span>
                  <span className="font-medium text-emerald-400">{(q.correct_answers ?? []).join(', ')}</span>
                </div>
              )}
              {q.explanation && (
                <p className="mt-2 text-xs leading-relaxed px-3 py-2 rounded-lg" style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>
                  {q.explanation}
                </p>
              )}
            </div>
          )}
          {q.type === 'true_false' && (
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span style={{ color: 'var(--text-3)' }}>Your answer:</span>
                <span className="font-medium" style={{ color: correct ? '#4ade80' : '#f87171' }}>{r.user_answer || 'Not answered'}</span>
              </div>
              {!correct && (
                <div className="flex items-center gap-2">
                  <span style={{ color: 'var(--text-3)' }}>Correct:</span>
                  <span className="font-medium text-emerald-400">{q.correct_answer}</span>
                </div>
              )}
              {q.explanation && (
                <p className="mt-2 text-xs leading-relaxed px-3 py-2 rounded-lg" style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>
                  {q.explanation}
                </p>
              )}
            </div>
          )}
          {q.type === 'short_answer' && (
            <div className="text-xs space-y-2">
              <div className="flex items-start gap-2">
                <span style={{ color: 'var(--text-3)', flexShrink: 0 }}>Your answer:</span>
                <span className="font-medium" style={{ color: r.user_answer ? 'var(--text-2)' : '#f87171' }}>{r.user_answer || 'Not answered'}</span>
              </div>
              {r.feedback && <p className="font-medium" style={{ color: dotColor }}>{r.feedback}</p>}
              {(r.keyPointsCovered?.length > 0 || r.keyPointsMissed?.length > 0) && (
                <div className="space-y-1">
                  {r.keyPointsCovered?.map((kp, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <span className="text-emerald-400 font-bold flex-shrink-0">✓</span>
                      <span style={{ color: 'var(--text-2)' }}>{kp}</span>
                    </div>
                  ))}
                  {r.keyPointsMissed?.map((kp, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <span className="text-red-400 font-bold flex-shrink-0">✗</span>
                      <span style={{ color: 'var(--text-3)' }}>{kp}</span>
                    </div>
                  ))}
                </div>
              )}
              {q.correct_answer && (
                <p className="px-3 py-2 rounded-lg leading-relaxed" style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>
                  <span style={{ color: 'var(--text-3)' }}>Model answer: </span>{q.correct_answer}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function fmt(iso) {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function toDisplayVerdict(v) {
  if (v === 'correct')   return 'Correct'
  if (v === 'partial')   return 'Partially Correct'
  if (v === 'incorrect') return 'Incorrect'
  return null
}

function mapApiAttempt(a) {
  const results = (a.answers ?? []).map(ans => ({
    is_correct:       ans.isCorrect,
    creditEarned:     ans.creditEarned,
    verdict:          toDisplayVerdict(ans.aiVerdict),
    user_answer:      ans.chosen ?? (ans.type === 'msq' ? [] : ''),
    keyPointsCovered: ans.keyPointsCovered ?? [],
    keyPointsMissed:  ans.keyPointsMissed  ?? [],
    feedback:         ans.feedback         ?? '',
    question: {
      type:            ans.type,
      question:        ans.question,
      options:         ans.options ?? null,
      correct_answer:  ans.correct_answer,
      correct_answers: ans.correct_answers,
      explanation:     ans.explanation ?? '',
      key_points:      ans.key_points ?? [],
      topic:           ans.topic ?? '',
      difficulty:      ans.difficulty ?? '',
    },
  }))
  return {
    id:       a._id,
    examName: a.examId?.name ?? '—',
    takenAt:  a.submittedAt,
    mode:     'virtual',
    total:    a.totalQ,
    score:    a.score,
    results,
  }
}

export default function ResultDetailPage() {
  const { id }   = useParams()
  const navigate = useNavigate()

  const [entry,   setEntry]   = useState(() => getResultById(id) ?? null)
  const [loading, setLoading] = useState(!entry)
  const [error,   setError]   = useState(false)

  useEffect(() => {
    if (entry) return
    api.get(`/api/users/attempts/${id}`)
      .then(data => setEntry(mapApiAttempt(data)))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <div className="px-8 sm:px-40 py-8 sm:py-12 text-sm" style={{ color: 'var(--text-3)' }}>Loading…</div>
    )
  }

  if (error || !entry) {
    return (
      <div className="px-8 sm:px-40 py-8 sm:py-12">
        <button onClick={() => navigate(-1)} className="btn-ghost mb-6 text-sm">← Back</button>
        <div className="card p-12 text-center">
          <p className="text-4xl mb-4">📭</p>
          <p className="text-base font-semibold" style={{ color: 'var(--text-1)' }}>Result not available</p>
        </div>
      </div>
    )
  }

  const results = entry.results ?? []
  const isMsqPartial = r => r.question.type === 'msq' && r.creditEarned > 0 && r.creditEarned < 1
  const correct = results.filter(r => r.is_correct === true || r.verdict === 'Correct').length
  const partial = results.filter(r => r.verdict === 'Partially Correct' || isMsqPartial(r)).length
  const wrong   = results.filter(r => r.is_correct === false && r.verdict !== 'Partially Correct' && !isMsqPartial(r)).length

  return (
    <div className="px-4 sm:px-40 py-6 sm:py-12 animate-fade-up"
         style={{ userSelect: 'none' }} onCopy={e => e.preventDefault()} onContextMenu={e => e.preventDefault()}>

      <button onClick={() => navigate(-1)} className="btn-ghost mb-6 text-sm">← Back</button>

      <div className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--brand)' }}>Answer Review</p>
        <h2 className="text-2xl font-black tracking-tight mb-1" style={{ color: 'var(--text-1)' }}>Review Answers</h2>
        <p className="text-sm" style={{ color: 'var(--text-3)' }}>
          {entry.examName} · {entry.mode === 'virtual' ? `⏱ ${STRINGS.modes.virtual}` : `📖 ${STRINGS.modes.practice}`}
          {entry.takenAt ? ` · ${fmt(entry.takenAt)}` : ''}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { v: results.length, l: 'Total',   c: 'var(--text-1)' },
          { v: correct,        l: 'Correct', c: '#4ade80' },
          { v: partial,        l: 'Partial', c: '#fbbf24' },
          { v: wrong,          l: 'Wrong',   c: '#f87171' },
        ].map(({ v, l, c }) => (
          <div key={l} className="card p-4 text-center">
            <div className="text-2xl font-black mb-0.5" style={{ color: c }}>{v}</div>
            <div className="text-xs" style={{ color: 'var(--text-3)' }}>{l}</div>
          </div>
        ))}
      </div>

      {results.length === 0 ? (
        <div className="card p-10 text-center">
          <p className="text-2xl mb-2">📭</p>
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>Detailed answers not available for this attempt.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          {results.map((r, i) => <AnswerRow key={i} r={r} idx={i} />)}
        </div>
      )}
    </div>
  )
}
